import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHistory } from "react-router-dom";
import { Link } from 'react-router-dom';
import { LIST } from '../../config/routes';
import { 
  LIMIT_PER_PERSON_DEFAULT, 
  QUANTITY_LIMIT_DEFAULT, 
  REDEEMABLE_PERIOD_DEFAULT 
} from '../../config/defaults';
import { post as internalPost } from '../../lib/iframeApi';
import { htmlToText } from 'html-to-text';

import ReactTooltip from 'react-tooltip';
import TextInput from '../Components/TextInput';
import TextArea from '../Components/TextArea';
import DatePicker from '../Components/DatePicker';
import Dropdown from '../Components/Dropdown';
import { openUploadWidget } from '../Components/CloudinaryService';

import * as actions from './DealForm.actions';
import * as listActions from '../DealList/DealList.actions';
import * as modalActions from '../ModalAlert/ModalAlert.actions';

const DealForm = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams();

  const dropdownOptions = useMemo(() => { return [
    { value: 'redemption_text', label: t('form_field_redemption_option_01') },
    { value: 'redemption_image', label: t('form_field_redemption_option_02') },
    { value: 'bar_code', label: t('form_field_redemption_option_03') }
  ]; }, [ t ]);

  const dispatch = useDispatch();
  const store = useSelector(store => store);
  const storeName = store.app.storeName;
  const storeId = store.app.storeId;
  const perPage = store.list.perPage;
  const allDeals = store.list.deals;
  const dealData = store.form.dealData;
  const formComplete = store.form.requestComplete;
  const [bar_code, setBarCode] = useState('');
  const [deal_image, setDealImage] = useState('');
  const [description, setDescription] = useState('');
  const [ends_at, setEndsAt] = useState('');
  const [limit_per_person, setLimitPerPerson] = useState(LIMIT_PER_PERSON_DEFAULT);
  const [quantity_limit, setQuantityLimit] = useState(QUANTITY_LIMIT_DEFAULT);
  const [redeemable_period, setRedeemablePeriod] = useState(REDEEMABLE_PERIOD_DEFAULT);
  const [redemption_image, setRedemptionImage] = useState('');
  const [redemption_text, setRedemptionText] = useState('');
  const [starts_at, setStartsAt] = useState('');
  const [terms_and_conditions, setTermsAndConditions] = useState('');
  const [title, setTitle] = useState('');
  const [redemptionMethod, setRedemptionMethod] = useState(dropdownOptions[0]);
  const [allowSubmit, setAllowSubmit] = useState(true);
  const [dealLoaded, setDealLoaded] = useState(false);
  const [spellingErrors, setSpellingErrors] = useState([]);
  const [highlightSpellingErrors, setHighlightSpellingErrors] = useState(false);
  const errorMsg = useSelector(errors => errors);

  useEffect(() => {
    if (id && allDeals.length && !dealLoaded) {
      const data = allDeals.find(item => item.deal_id.toString() === id);
      if (data) {
        setDealLoaded(true);
        setBarCode((data.bar_code) ? data.bar_code : '');
        setDealImage((data._links.image.href) ? data._links.image.href : '');
        setDescription((data.description) ? data.description : '');
        setEndsAt((data.ends_at) ? data.ends_at.replace('Z','') : '2000-01-01T00:00:00+00:00');
        setLimitPerPerson((data.limit_per_person) ? data.limit_per_person : 0);
        setQuantityLimit((data.quantity_limit) ? data.quantity_limit : 0);
        setRedeemablePeriod((data.redeemable_period) ? data.redeemable_period : 60);
        setRedemptionImage((data.redemption_image) ? data.redemption_image : '');
        setRedemptionText((data.redemption_text) ? data.redemption_text : '');
        setStartsAt((data.starts_at) ? data.starts_at.replace('Z','') : '2000-01-01T00:00:00+00:00');
        setTermsAndConditions((data.terms_and_conditions) ? data.terms_and_conditions : '');
        setTitle(data.title);
        // set the redemption method
        if (data.redemption_image) setRedemptionMethod(dropdownOptions[1]);
        else if (data.bar_code) setRedemptionMethod(dropdownOptions[2]);
      }
    }
  }, [
    id, 
    allDeals, 
    dealLoaded,
    dropdownOptions,
    setBarCode, 
    setDealImage, 
    setDescription, 
    setEndsAt, 
    setLimitPerPerson, 
    setQuantityLimit, 
    setRedeemablePeriod, 
    setRedemptionImage, 
    setRedemptionText, 
    setStartsAt,
    setTermsAndConditions, 
    setTitle, 
    setRedemptionMethod
  ])

  useEffect(() => {
    let enabled = Boolean(
      title.length && 
      redeemable_period > 0 && 
      description.length && 
      terms_and_conditions.length && 
      starts_at.length && 
      ends_at.length && 
      (
        (redemptionMethod.value === 'redemption_text' && redemption_text.length) || 
        (redemptionMethod.value === 'redemption_image' && redemption_image.length) || 
        (redemptionMethod.value === 'bar_code' && bar_code.length)
      )
    );
    setAllowSubmit(enabled);
  }, [
    title, 
    redeemable_period, 
    description, 
    terms_and_conditions, 
    starts_at, 
    ends_at, 
    redemptionMethod, 
    redemption_text, 
    redemption_image, 
    bar_code, 
    allowSubmit
  ])

  useEffect(() => {
    if (formComplete) {
      setSpellingErrors([]);
      setHighlightSpellingErrors(false);
      dispatch(actions.formReset());
      dispatch(listActions.getDealList(storeId, perPage));
      history.push(LIST);
    }
  }, [dispatch, formComplete, history, storeId, perPage, spellingErrors, highlightSpellingErrors])

  const onChangeHandler = (name, data) => {
    switch (name) {
      case 'title':
        setTitle(data);
        break;
      case 'starts_at':
        data = data+'T00:00:00';
        setStartsAt(data);
        data = data+"Z";
        break;
      case 'ends_at':
        data = data+'T23:59:59Z';
        setEndsAt(data);
        break;
      case 'redeemable_period':
        setRedeemablePeriod(data);
        data = parseIntValid(data);
        break;
      case 'quantity_limit':
        setQuantityLimit(data);
        data = parseIntValid(data);
        break;
      case 'limit_per_person':
        setLimitPerPerson(data);
        data = parseIntValid(data);
        // extra logic here to set limit_per_person_period correcly
        if (data > 0) dealData['limit_per_person_period'] = 0
        else dealData['limit_per_person_period'] = 24
        break;
      case 'bar_code':
        setBarCode(data);
        break;
      case 'description':
        setDescription(data);
        break;
      case 'terms_and_conditions':
        setTermsAndConditions(data);
        break;
      case 'redemption_text':
        setRedemptionText(data);
        break;
      default:
        break;
    }
    // trim any strings after we set the local vailable to ensure 
    // the field still allows for spaces in mutli-word sentences
    if (typeof data === 'string' && name != 'title') data = String(data).trim();

    dealData[name] = data;
    dispatch(actions.updateFieldData(dealData))
  }

  const imageFieldClickHandler = (event) => {
    const fieldName = event.currentTarget.name;
    openUploadWidget((error, result) => {
      if (!error) {
        if (result.event === 'success') {
          const imageUrl = result.info.secure_url;
          if (fieldName === 'image_ref') {
            setDealImage(imageUrl);
            // need to also set mobilemobile_offer_visualimage_ref to the same value
            dealData['mobile_offer_visual'] = imageUrl;
          } else {
            setRedemptionImage(imageUrl);
          }
          dealData[fieldName] = imageUrl;
        }
      } else {
        console.log(`CLOUDINARY ERROR: ${error}`);
      }
    });
  }

  const handleValidation = () => {
    const errors = { title: []};
    let formIsValid = true;

    if(title.length > 100) {
      formIsValid = false;
      errors.title.push(t('form_field_title_maximum_length'));
    }

    if(/^\s/.test(title) || /\s$/.test(title)) {
      formIsValid = false;
      errors.title.push(t('form_field_title_trailing_spaces'));
    }
    dispatch(actions.setErrorMessage(errors))
    return formIsValid;
  }

  const submitHandler = () => {
    if (handleValidation()) {
      internalPost('spelling/check', { lang: store.app.selectedLanguage, fields: getSpellCheckFields() })
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          if (data.length) {
            setSpellingErrors(data);
            processSpellingErrors();
          } else { 
            upsertDeal();
          }
        })
        .catch((error) => {
          console.log(error);
          upsertDeal();
        })
    }
  }

  const upsertDeal = () => {
    if (id) updateDeal();
    else createDeal();
  }

  const createDeal = () => {
    // add required params not found on form
    dealData['redeemable'] = true;
    dealData['published_at'] = moment(new Date()).startOf('day').format('YYYY-MM-DD[T00:00:00Z]');
    dealData['support_diffusions'] = [1043];
    dealData['state_event'] = 'approve';
    dealData['stores'] = [{
      centre_id: store.app.centerId,
      retailer_id: store.app.retailerId,
      store_id: storeId
    }]
    // if redeemable period not set, need to set default
    if (!dealData['redeemable_period']) {
      dealData['redeemable_period'] = parseInt(REDEEMABLE_PERIOD_DEFAULT);
    }
    // if image not added, need to fallback to store logo
    if (!dealData['image_ref']) {
      dealData['image_ref'] = dealData['mobile_offer_visual'] = store.app.retailerLogo;
    }
    dispatch(actions.createDeal(dealData));
  }

  const updateDeal = () => {
    dispatch(actions.editDeal(id, dealData));
  }

  const processSpellingErrors = () => {
    const alertProps = {
      header: t('alert_spelling_error_header'),
      body: t('alert_spelling_error_body'),
      ok_button: t('alert_spelling_error_yes'),
      cancel_button: t('alert_spelling_error_no'),
      hideCloseButton: true,
      callback: modalCallback
    }
    dispatch(modalActions.setupModal(alertProps, true));
  }

  const modalCallback = (confirm) => {
    if (confirm) {
      setHighlightSpellingErrors(true);
    } else {
      upsertDeal();
    }
  }

  const parseIntValid = (val) => {
    let parsed = parseInt(val);
    return (!isNaN(parsed)) ? parsed : 0;
  }

  const getSpellCheckFields = () => {
    return {
      'description': getRawText(description),
      'redemption_text': getRawText(redemption_text),
      'terms_and_conditions': getRawText(terms_and_conditions),
      'title': getRawText(title)
    }
  }

  const getRawText = (val) => {
    return htmlToText(val, {
      wordwrap: false,
      tables: true,
      tags: {
        a: { options: { ignoreHref: true } },
        ul: { options: { itemPrefix: ' ' } },
        dataTable: { options: { rowSpacing: 1, colSpacing: 1 } },
        table: { options: { rowSpacing: 1, colSpacing: 1 } }
      }
    }).replace(/(?:\r\n|\r|\n|[^\w\s])/g, '').replace(/\s+/g, ' ');
  }

  const findAttribute = (array, attr, value) => {
    for (let i = 0; i < array.length; i += 1) {
      if (array[i][attr] === value) {
        return i;
      }
    }
    return -1;
  }

  return (
    <div className="page-container">
      <div className="main-header"></div>
      <div className="page-header page-header--fixed">
        <div className="aurora-row">
          <div className="aurora-col--8">
            <h1 className="coral-Heading coral-Heading--1">
              <div className="u-s-ml-small">
                { ((id) ? t('form_title_edit') : t('form_title_create'))+` ${storeName}` }
              </div>
            </h1>
          </div>
          <div className="aurora-col--4 page-header__menu">
            <div className="right">
              <Link className="coral-Form-field coral-Button coral-Button--default" to={ LIST }>
                { t('button_cancel') }
              </Link>
              <div className="u-display-inline-block u-s-ml-base">
                <div data-tip data-for="submit-tip">
                  <button id="btnSave" className="coral-Form-field coral-Button coral-Button--primary" onClick={ submitHandler } disabled={ !allowSubmit }>
                    { t('button_save') }
                  </button>
                </div>
                <ReactTooltip id="submit-tip" place="bottom" type="dark" effect="solid" disable={ allowSubmit }>
                  <span>{ t('form_button_tooltip') }</span>
                </ReactTooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="aurora-row coral-Form--fixed">
        <div className="aurora-col--12"></div>
      </div>
      <div className="coral-Form coral-Form--vertical">
      <div className="page-content">
        <section className="coral-Form-fieldset">
          <div className="aurora-row">
            <div className="aurora-col--6">
              <TextInput
                name="title"
                id="title"
                label={ t('form_field_title') }
                value={ title }
                data-required={ true }
                data-required-label={ t('form_field_required') }
                onChange={ (event) => { onChangeHandler('title', event.target.value) } }
                error={ errorMsg.form.errors }
              />
              <div className={`spelling-error ${ (highlightSpellingErrors && findAttribute(spellingErrors, 'field', 'title') !== -1) ? '' : 'hidden' }`} style={{ marginTop: '-6px' }}>
                { t('form_field_alert_spelling_error') }
              </div>
            </div>
            <div className="aurora-col--3">
              <DatePicker 
                name="starts_at"
                id="starts_at"
                label={ t('form_field_start_date') }
                value={ starts_at }
                data-required={ true }
                data-required-label={ t('form_field_required') }
                onChange={ onChangeHandler }
                disabled={ (id !== undefined) }
              />
            </div>
            <div className="aurora-col--3">
              <DatePicker 
                name="ends_at"
                id="ends_at"
                label={ t('form_field_end_date') }
                value={ ends_at }
                data-required={ true }
                data-required-label={ t('form_field_required') }
                onChange={ onChangeHandler }
              />
            </div>
          </div>
          <div className="aurora-row">
            <div className="aurora-col--6">
              <TextInput
                name="redeemable_period"
                id="redeemable_period"
                label={ t('form_field_expiration_timer') }
                value={ redeemable_period }
                type="number"
                data-required={ true }
                data-required-label={ t('form_field_required') }
                onChange={ (event) => { onChangeHandler('redeemable_period', event.target.value) } }
              />
            </div>
            <div className="aurora-col--3">
              <TextInput
                name="quantity_limit"
                id="quantity_limit"
                label={ t('form_field_quantity_limit') }
                type="number"
                value={ quantity_limit }
                onChange={ (event) => { onChangeHandler('quantity_limit', event.target.value) } }
                disabled={ (id !== undefined) }
              />
            </div>
            <div className="aurora-col--3">
              <TextInput
                name="limit_per_person"
                id="limit_per_person"
                label={ t('form_field_customer_limit') }
                type="number"
                value={ limit_per_person }
                onChange={ (event) => { onChangeHandler('limit_per_person', event.target.value) } }
                disabled={ (id !== undefined) }
              />
            </div>
          </div>
          <div className="aurora-row">
            <div className="aurora-col--6">
              <TextInput
                name="image_ref"
                id="image_ref"
                label={ t('form_field_deal_image') }
                value={ deal_image }
                readOnly={ true }
                onClick={ (event) => { imageFieldClickHandler(event) } }
                style={{ cursor: 'pointer' }}
              />
            </div>
          </div>
        </section>
      </div>
      <div className="page-content">
        <section className="coral-Form-fieldset">
          <div className="aurora-row">
            <div className="aurora-col--12">
              <TextArea
                name="description"
                id="description"
                label={ t('form_field_description') }
                value={ description }
                data-required={ true }
                data-required-label={ t('form_field_required') }
                onChange={ onChangeHandler }
              />
              <div className={`spelling-error ${ (highlightSpellingErrors && findAttribute(spellingErrors, 'field', 'description') !== -1) ? '' : 'hidden' }`}>
                { t('form_field_alert_spelling_error') }
              </div>
            </div>
          </div>
        </section>
        <section className="coral-Form-fieldset">
          <div className="aurora-row">
            <div className="aurora-col--12">
              <TextArea
                name="terms_and_conditions"
                id="terms_and_conditions"
                label={ t('form_field_terms_and_conditions') }
                value={ terms_and_conditions }
                data-required={ true }
                data-required-label={ t('form_field_required') }
                onChange={ onChangeHandler }
              />
              <div className={`spelling-error ${ (highlightSpellingErrors && findAttribute(spellingErrors, 'field', 'terms_and_conditions') !== -1) ? '' : 'hidden' }`}>
                { t('form_field_alert_spelling_error') }
              </div>
            </div>
          </div>
        </section>
      </div>
      <div className="page-content">
        <section className="coral-Form-fieldset">
          <div className="aurora-row">
            <div className="aurora-col--6">
              <Dropdown
                label={ t('form_field_redemption_method') }
                id="redemption_method"
                options={ dropdownOptions }
                value={ redemptionMethod }
                data-required={ true }
                data-required-label={ t('form_field_required') }
                onChange={ (selectedValue) => {
                  setRedemptionMethod(selectedValue);
                }}
              />
            </div>
            <div className="aurora-col--6">
              <TextInput
                id="bar_code"
                name="bar_code"
                label={ t('form_field_barcode') }
                value={ bar_code }
                data-required={ true }
                data-required-label={ t('form_field_required') }
                hidden={ redemptionMethod.value !== 'bar_code' }
                onChange={ (event) => { onChangeHandler('bar_code', event.target.value) } }
              />
              <TextInput
                id="redemption_image"
                name="redemption_image"
                label={ t('form_field_redemption_image') }
                value={ redemption_image }
                data-required={ true }
                data-required-label={ t('form_field_required') }
                hidden={ redemptionMethod.value !== 'redemption_image' }
                readOnly={ true }
                onClick={ (event) => { imageFieldClickHandler(event) } }
                style={{ cursor: 'pointer' }}
              />
            </div>
          </div>
          <div className="aurora-row">
            <div className="aurora-col--12" style={{ paddingTop: '20px' }}>
              <TextArea
                id="redemption_text"
                name="redemption_text"
                label={ t('form_field_redemption_instructions') }
                value={ redemption_text }
                data-required={ true }
                data-required-label={ t('form_field_required') }
                hidden={ redemptionMethod.value !== 'redemption_text' }
                onChange={ onChangeHandler }
              />
              <div className={`spelling-error ${ (highlightSpellingErrors && findAttribute(spellingErrors, 'field', 'redemption_text') !== -1) ? '' : 'hidden' }`}>
                { t('form_field_alert_spelling_error') }
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
    </div>
  )
}

DealForm.propTypes = {
  bar_code: PropTypes.string,
  deal_id: PropTypes.number,
  description: PropTypes.string,
  ends_at: PropTypes.string,
  limit_per_person: PropTypes.number,
  quantity_limit: PropTypes.number,
  redemption_image: PropTypes.string,
  redeemable_period: PropTypes.number,
  redemption_text: PropTypes.string,
  starts_at: PropTypes.string,
  terms_and_conditions: PropTypes.string,
  title: PropTypes.string,
  _links: PropTypes.shape({
    image: PropTypes.shape({
      href: PropTypes.string
    })
  })
}

DealForm.defaultProps = {
  bar_code: '',
  deal_id: 0,
  description: '',
  ends_at: '2000-01-01T00:00:00+00:00',
  limit_per_person: 0,
  quantity_limit: 0,
  redemption_image: '',
  redeemable_period: 60,
  redemption_text: '',
  starts_at: '2000-01-01T00:00:00+00:00',
  terms_and_conditions: '',
  title: '',
  _links: {
    image: {
      href: ''
    }
  }
}

export default DealForm;